/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { connect, ConnectedProps } from 'react-redux';
import {
  Link as RouterLink,
  NavLink,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useResolvedPath,
} from 'react-router';

import { Breadcrumbs, Button, Grid, Tab, Tabs, Typography } from '@mui/material';

import { ProjectManageClustersList } from '../../components/ClustersList/ProjectManage/ProjectClustersList';
import { BasicRoutes } from '../../constants/basicRoutes';
import { Namespaces } from '../../i18n';
import { RootState } from '../../store';
import { useNavTabsStyles } from '../../styles/navigationTabs';
import { EntityId } from '../../types/api/EntityId';
import { prepareRoutePath } from '../../utils/prepareRoutePath';
import { fetchProject, fetchProjectsWithClusters } from '../../utils/queryFns';
import { useControlOverlays } from '../../utils/useControlOverlays';
import { useDocumentTitle } from '../../utils/useDocumentTitle';
import { EditProjectForm } from './components/EditProjectForm/EditProjectForm';
import { MembersTab } from './components/MembersTab/Members';
import { ManageProjectTabs, tabToRouteBinding } from './ManageProjectPage.consts';
import { createManageProjectPageStyles } from './ManageProjectPage.styles';

const mapStateToProps = (state: RootState) => ({
  organizationDetails: state.organizationDetails,
});
const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type PathParamsType = {
  organization_id: string;
  project_id: string;
};

type IProps = PropsFromRedux;

const ManageProjectPageComponent = memo<IProps>(props => {
  const { t } = useTranslation([Namespaces.OrganizationAdmin, Namespaces.Navigation]);
  const classes = createManageProjectPageStyles();

  const { organizationDetails } = props;
  const location = useLocation();
  const baseUrl = useResolvedPath('').pathname;
  const params = useParams<PathParamsType>();
  const navigate = useNavigate();

  const organizationId = params.organization_id!;
  const projectId = params.project_id!;
  const organizationIsFetching = organizationDetails?.isFetching;
  const organization = organizationDetails?.options?.payload || null;
  const navTabsClasses = useNavTabsStyles();

  const { doOpenCreateServiceOverlay } = useControlOverlays();

  const handleManageClusterClick = (clusterId: EntityId) => {
    void navigate(
      prepareRoutePath(BasicRoutes.CLUSTER_PAGE, {
        ':organization_id': organizationId,
        ':project_id': projectId,
        ':cluster_id': clusterId,
      }),
    );
  };

  const projectQuery = useQuery([fetchProject.key, { organizationId, projectId }], fetchProject.fn);
  const organizationProjectsClustersQuery = useQuery(
    [fetchProjectsWithClusters.key, { organizationId }],
    fetchProjectsWithClusters.fn,
  );

  const project = useMemo(() => projectQuery.data?.payload || null, [projectQuery.data]);

  useDocumentTitle(t(`${Namespaces.Common}:page_titles.project_manage`, { project: project?.name }));

  const clusters = useMemo(
    () => organizationProjectsClustersQuery.data?.data.payload.find(proj => proj.id === projectId)?.clusters || [],
    [projectId, organizationProjectsClustersQuery.data],
  );

  const getPathForTab = (tab: ManageProjectTabs) => {
    return `${baseUrl}/${tabToRouteBinding[tab]}`;
  };

  return (
    <Grid container>
      <Grid item xs={12} classes={{ item: classes.wrapperBreadcrumbs }}>
        <Breadcrumbs>
          <RouterLink to="/">{t('navigation:page.home')}</RouterLink>
          <RouterLink
            to={prepareRoutePath(BasicRoutes.ORGANIZATION_PAGE, {
              ':organization_id': organizationId,
            })}
          >
            {!organizationIsFetching && organization?.name}
          </RouterLink>
          <RouterLink
            to={prepareRoutePath(BasicRoutes.PROJECT_PAGE, {
              ':organization_id': organizationId,
              ':project_id': projectId,
            })}
          >
            {project?.name}
          </RouterLink>
          <Typography>{t('projects.management.manage_project')}</Typography>
        </Breadcrumbs>
      </Grid>
      <Typography classes={{ h3: classes.h3 }} variant="h3">
        {t('projects.management.manage_project')}
      </Typography>
      <Grid item xs={12}>
        <Tabs
          aria-label="project manage tabs"
          indicatorColor="primary"
          scrollButtons={false}
          value={location.pathname}
          classes={{ root: navTabsClasses.root }}
          className={classes.tabsRoot}
          TabIndicatorProps={{ style: { display: 'none' } }}
        >
          <Tab
            value={baseUrl}
            label={t('management.details_tab')}
            component={NavLink}
            to={tabToRouteBinding[ManageProjectTabs.General]}
          />
          <Tab
            value={getPathForTab(ManageProjectTabs.Clusters)}
            label={t('projects.management.clusters_tab')}
            component={NavLink}
            to={getPathForTab(ManageProjectTabs.Clusters)}
          />
          <Tab
            value={getPathForTab(ManageProjectTabs.Members)}
            label={t('projects.management.members_tab')}
            component={NavLink}
            to={getPathForTab(ManageProjectTabs.Members)}
            data-cy="manage-project-members-tab"
          />
        </Tabs>
        <div>
          <Routes>
            <Route index element={project && <EditProjectForm organizationId={organizationId} project={project} />} />
            <Route
              path={tabToRouteBinding[ManageProjectTabs.Clusters]}
              element={
                <>
                  <ProjectManageClustersList clusters={clusters} onManageClick={id => handleManageClusterClick(id)} />
                  <Button onClick={() => doOpenCreateServiceOverlay()} className={classes.createButton}>
                    {t('management.clusters_list.create')}
                  </Button>
                </>
              }
            />
            <Route
              path={tabToRouteBinding[ManageProjectTabs.Members]}
              element={<MembersTab organizationId={organizationId} projectId={projectId} />}
            />
          </Routes>
        </div>
      </Grid>
    </Grid>
  );
});

export const ManageProjectPage = connector(ManageProjectPageComponent);
